import { Box, CircularProgress, Typography } from "@mui/material";
import OneWeekCalendar from "../Calendar";
import moment, { Moment } from "moment";
import { useEffect, useState } from "react";
import appointmentServices from "servers/appointment";
import { IBookingSettings, ISelectedService } from "pages/booking/interface";
import empty_schedule from "assets/images/schedule.png";
import TimeSection from "../TimeSection";

interface IProps {
  setActiveTab: (agr: number) => void;
  selectedDateTime: { date?: Moment; time: string };
  setSelectedDateTime: (agr: { date?: Moment; time: string }) => void;
  selectedServices: Array<ISelectedService>;
  timezone: string;
  bookingSettings: IBookingSettings;
}

const PickDayAndTime = ({
  setActiveTab,
  selectedDateTime,
  setSelectedDateTime,
  selectedServices,
  timezone,
  bookingSettings,
}: IProps) => {
  const [availableTime, setAvailableTime] = useState<{
    [key: string]: Array<string>;
  }>({});

  const handlePickDate = (date?: Moment) => {
    setSelectedDateTime({ time: selectedDateTime.time, date });
  };

  const [date, setDate] = useState({ startDate: 0, endDate: 0 });
  const [isLoading, setIsLoading] = useState(false);

  const getAvailableTimeOfStaffs = async () => {
    setIsLoading(true);
    try {
      const response = await appointmentServices.getAvailableTime({
        staffIds: Array.from(
          new Set(selectedServices.map((service) => service.staffId))
        ),
        startWeek: date.startDate.toString(),
        endWeek: date.endDate.toString(),
        duration: Math.max(
          ...(Object.values(
            selectedServices.reduce(
              (maxDurations: Record<string, number>, service) => {
                const { staffId, time, bufferTime } = service;
                const totalTime =
                  time + (bookingSettings.includeBufferTime ? bufferTime : 0);
                maxDurations[staffId] =
                  (maxDurations[staffId] || 0) + totalTime;
                return maxDurations;
              },
              {}
            )
          ) as number[])
        ),
        timezone: timezone || "US/Central",
      });
      if (response.data.success) {
        setAvailableTime(response.data.data);
        const dates = Object.keys(response.data.data);
        let defaultDate;
        for (let i = 0; i < dates.length; i++) {
          const date = dates[i];
          if (moment(date, "YYYY-MM-DD").isSameOrAfter(moment(), "day")) {
            defaultDate = moment(date);
            break;
          }
        }
        setSelectedDateTime({
          time: "",
          date: defaultDate,
        });
      }
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    date.startDate !== 0 && date.endDate !== 0 && getAvailableTimeOfStaffs();
  }, [date.startDate, date.endDate]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        animation: "0.75s slide-left",
      }}
    >
      <OneWeekCalendar
        selectedDateTime={selectedDateTime}
        handlePickDate={handlePickDate}
        availableTime={availableTime}
        setDate={setDate}
        date={date}
        maxAdvanceDays={bookingSettings.maxAdvanceDays}
        isLoading={isLoading}
      />
      {isLoading ? (
        <Typography textAlign="center" mt={1}>
          <CircularProgress size="26px" />
        </Typography>
      ) : Object.keys(availableTime).length > 0 ? (
        <TimeSection
          availableTime={availableTime}
          setActiveTab={setActiveTab}
          selectedDateTime={selectedDateTime}
          setSelectedDateTime={setSelectedDateTime}
          isLoading={isLoading}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: "20px",
            marginTop: 2,
            gap: 1,
          }}
        >
          <img src={empty_schedule} width={85} height={85} />
          <Typography fontWeight={600} fontSize={18} color="#637381">
            No Available Time
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PickDayAndTime;
